<template>
  <div class="zmanim-logo-clock__container">
    <div
      v-observe-visibility="visibilityChanged"
      class="zmanim-logo-clock__layer"
    >
      <img
        class="zmanim-logo__image zmanim-logo-clock__face"
        src="@/assets/clock/face@3x.png"
      />
      <img
        :style="minuteHandStyle"
        class="zmanim-logo__image zmanim-logo-clock__component zmanim-logo-clock__component-hand_minute"
        src="@/assets/clock/minute@3x.png"
      />
      <img
        :style="hourHandStyle"
        class="zmanim-logo__image zmanim-logo-clock__component zmanim-logo-clock__component-hand_hour"
        src="@/assets/clock/hour@3x.png"
      />
      <img
        v-if="!!seconds"
        :style="secondHandStyle"
        class="zmanim-logo__image zmanim-logo-clock__component zmanim-logo-clock__component-hand_second"
        src="@/assets/clock/seconds@3x.png"
      />
      <img
        class="zmanim-logo__image zmanim-logo-clock__component zmanim-logo-clock__component-screw"
        src="@/assets/clock/screw@3x.png"
      />
    </div>
    <div v-if="dots" class="zmanim-logo-indicator__container">
      <zmanim-solar-placemark
        v-for="(index, zman) in [nextZman]"
        :title="zman.title"
        :time="zman.time"
        :key="`z${zman.title}-${index}`"
        :style="styleForZman(zman)"
        background="light"
      />
    </div>
  </div>
</template>

<script>
import { brooklyn } from '@/lib/locations'
import { log, DateBasedAnimation } from '@/lib/mixins'
import ZmanimList from '@/lib/zmanimList'
import ZmanimSolarPlacemark from './ZmanimSolarPlacemark'

export default {
  name: 'ZmanimClock',
  components: { ZmanimSolarPlacemark },
  mixins: [DateBasedAnimation, log],
  props: {
    dots: {
      type: Boolean,
      default: true
    },
    seconds: {
      type: Boolean,
      default: false
    },
    location: {
      type: Object,
      default: () => brooklyn
    }
  },
  data () {
    return {
      loggingEnabled: false,
      index: 0
    }
  },
  computed: {
    hourHandStyle () {
      const angle = this.hourRotation
      return {
        transition: 'transform 300ms ease',
        transformOrigin: 'bottom middle',
        transform: `scale(0.65) rotate(${angle}deg)`
      }
    },
    minuteHandStyle () {
      const angle = this.minuteRotation
      return {
        transition: 'transform 300ms ease',
        transformOrigin: 'bottom middle',
        transform: `scale(0.65) rotate(${angle}deg)`
      }
    },
    secondHandStyle () {
      const angle = this.secondsRotation
      return {
        // transition: 'transform 900ms linear', // slow, continuous movement
        transition: 'transform 50ms ease-in',
        transformOrigin: 'bottom middle',
        transform: `scale(0.65) rotate(${angle}deg)`
      }
    },
    zmanLandmarkStyle () {
      const angle = this.secondsRotation
      return {
        // transition: 'transform 900ms linear', // slow, continuous movement
        transition: 'transform 50ms ease-in',
        transformOrigin: 'bottom middle',
        transform: `scale(0.65) rotate(${angle}deg)`
      }
    },
    secondsSinceMidnight () {
      return this.secondsSinceMidnightAtDate(this.date)
    },
    minutesSinceMidnight () {
      const elapsed = this.secondsSinceMidnight / 60
      return elapsed
    },
    hoursSinceMidnight () {
      const elapsed = this.minutesSinceMidnight / 60
      this.log('elapsed hours: ', elapsed)
      return elapsed
    },
    degreesPerSecond () {
      const degreesPerSecond = 360 / 60 // 6º
      return degreesPerSecond
    },
    degreesPerHour () {
      const degrees = 360 / 12 // 30º
      return degrees
    },
    // https://en.wikipedia.org/wiki/Clock_angle_problem
    hourRotation () {
      return this.degreesPerHour * this.hoursSinceMidnight
    },
    minuteRotation () {
      return this.degreesPerSecond * this.minutesSinceMidnight
    },
    secondsRotation () {
      const angle = this.degreesPerSecond * this.secondsSinceMidnight
      return angle
    },
    zmanim () {
      const location = this.location
      const zmanimList = new ZmanimList(location)
      return zmanimList.zmanimItems
    },
    nextZman () {
      const now = new Date()
      const next = this.zmanim.find(zman => zman.date > now)
      return next
    }
  },
  created () {
    this.animate()
  },
  methods: {
    secondsSinceMidnightAtDate (date) {
      const midnight = new Date().setHours(0, 0, 0, 0)
      const elapsed = (date - midnight) / 1000.0
      this.log(this.$options.name, ': seconds since midnight: ', elapsed)
      return elapsed
    },
    angleForZman (zman) {
      const date = zman.date
      const seconds = this.secondsSinceMidnightAtDate(date)
      const angle = seconds * this.degreesPerSecond
      return angle // Add 90º to compensate for top-left default position
    },
    styleForZman (zman, compensatingForParentRotation = false) {
      const angle = compensatingForParentRotation
        ? -1.0 * this.angleForZman(zman)
        : this.angleForZman(zman)
      const diameter = 5.36
      const radius = diameter / 2.0
      const y = Math.sin(angle) * radius
      const x = Math.cos(angle) * radius

      const style = {
        transform: `translateX(${x}rem) translateY(${y}rem)`,
        transition: 'transform 300ms ease'
      }

      return style
    }
  }
}
</script>

<style scoped>
/** A wrapper for the entire component */
.zmanim-logo-clock__container {
  width: 5.63rem;
  height: 5.63rem;
  flex-grow: 0;
  flex-shrink: 0;
}
/** The clock itself */
.zmanim-logo-clock__layer {
  display: flex;
  position: relative;
  width: 5.63rem;
  height: 5.63rem;
  align-content: center;
  justify-content: center;
  flex-grow: 0;
  flex-shrink: 0;
}

.zmanim-logo-clock__layer .zmanim-logo__image {
  position: absolute;
  display: flex;
  justify-content: center;
  flex-grow: 0;
  flex-shrink: 0;
  align-self: center;
  width: 5.63rem;
  height: 5.63rem;
}

.zmanim-logo-clock__face {
  margin-top: 7%;
}

/* Subtle interactions on hover... */
.zmanim-logo-clock__component {
  --zmn-component-scale: 0.65;
  transform: scale(var(--zmn-component-scale));
  -webkit-transition: --webkit-filter 300ms ease;
  transition: filter 300ms ease;
}

.zmanim-logo-clock__component-hand_hour {
  /* source: https://stackoverflow.com/a/23130537 */
  -webkit-filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.11));
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.11));
}

.zmanim-logo-clock__layer:hover .zmanim-logo-clock__component-hand_hour {
  -webkit-filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.12));
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.12));
}

.zmanim-logo-clock__component-hand_minute {
  -webkit-filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.08));
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.08));
}

.zmanim-logo-clock__layer:hover .zmanim-logo-clock__component-hand_minute {
  -webkit-filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
}

.zmanim-logo-clock__component-hand_second {
  -webkit-filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.05));
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.05));
}

.zmanim-logo-clock__layer:hover .zmanim-logo-clock__component-hand_second {
  -webkit-filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.08));
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.08));
}

.zmanim-logo-clock__component-screw {
  -webkit-filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.02));
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.02));
}

.zmanim-logo-clock__layer:hover .zmanim-logo-clock__component-screw {
  -webkit-filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.07));
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.07));
}

/** Dots around the clock */
.zmanim-logo-indicator__container {
  position: absolute;
  display: flex;
  justify-content: center;
  align-content: center;
  align-items: center;
  flex-grow: 0;
  flex-shrink: 0;
  width: 5.63rem;
  height: 5.63rem;
  margin-top: -5.63rem;
}

.zmanim-logo-clock__container .zmanim-logo-indicator__container {
  transform: scale(0.2);
  opacity: 0;
  transition: transform 300ms ease, opacity 300ms ease;
}

.zmanim-logo-clock__container:hover .zmanim-logo-indicator__container {
  transform: scale(0.9);
  opacity: 1;
}

@keyframes orbit {
  0% {
    transform: scale(0.2);
    opacity: 0;
  }

  70% {
    transform: scale(1);
    opacity: 1;
  }

  100% {
    transform: scale(0.8);
    opacity: 1;
  }
}
</style>
